import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, Phone, Loader2, Moon, Shield, Heart, Brain, Volume2, Stethoscope, Square, CheckSquare, ClipboardCheck, Users } from 'lucide-react';

const FORMSPREE_ID = 'mvkpjgoe';

// Symptom-to-quiz mapping: maps symptom IDs to quiz question IDs and suggested scores
const symptomToQuizMap = {
  1: { questionId: 1, score: 3 }, // Loud snoring -> Q1 (snore loudly) = Frequently
  2: { questionId: 2, score: 3 }, // Gasping/choking -> Q2 (stop breathing) = Frequently
  3: { questionId: 4, score: 2 }, // Morning headaches -> Q4 (wake with headaches) = A few times a week
  4: { questionId: 7, score: 2 }, // High blood pressure -> Q7 (blood pressure) = Yes, controlled
  5: { questionId: 3, score: 3 }, // Daytime exhaustion -> Q3 (tired during day) = Almost every day
  6: { questionId: 2, score: 3 }, // Witnessed breathing pauses -> Q2 (stop breathing) = Frequently
};

const symptoms = [
  { id: 1, icon: Volume2, text: 'Loud, chronic snoring' },
  { id: 2, icon: Moon, text: 'Gasping or choking during sleep' },
  { id: 3, icon: Brain, text: 'Morning headaches & brain fog' },
  { id: 4, icon: Heart, text: 'High blood pressure' },
  { id: 5, icon: Shield, text: 'Daytime exhaustion despite full sleep' },
  { id: 6, icon: Stethoscope, text: 'Witnessed breathing pauses' },
];

const SleepApneaSection = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ firstName: '', email: '', phone: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [checkedSymptoms, setCheckedSymptoms] = useState([]);
  const [showFloatingBadge, setShowFloatingBadge] = useState(false);
  const [partnerSnores, setPartnerSnores] = useState(false);

  const toggleSymptom = (id) => {
    setCheckedSymptoms(prev => 
      prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
    );
  };

  const checkedCount = checkedSymptoms.length;

  // Show floating badge when symptoms are checked and user scrolls away from section
  useEffect(() => {
    if (checkedCount === 0) {
      setShowFloatingBadge(false);
      return;
    }

    const handleScroll = () => {
      const section = document.getElementById('sleep-apnea');
      if (section) {
        const rect = section.getBoundingClientRect();
        // Show badge when section is mostly out of view
        setShowFloatingBadge(rect.bottom < 100 || rect.top > window.innerHeight - 100);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial state
    return () => window.removeEventListener('scroll', handleScroll);
  }, [checkedCount]);

  // Build quiz URL with pre-filled symptoms
  const getQuizUrl = () => {
    if (checkedCount === 0) return '/sleep-apnea-quiz';
    const prefill = checkedSymptoms.map(id => symptomToQuizMap[id]).filter(Boolean);
    const params = new URLSearchParams();
    prefill.forEach(p => params.append('q', `${p.questionId}:${p.score}`));
    return `/sleep-apnea-quiz?${params.toString()}`;
  };

  const handleTakeQuiz = () => {
    navigate(getQuizUrl());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch(`https://formspree.io/f/${FORMSPREE_ID}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          _subject: 'Sleep Apnea Lead — Homepage Section',
          'Lead Type': 'Sleep Apnea Consultation (Homepage)',
          'First Name': formData.firstName,
          'Email': formData.email,
          'Phone': formData.phone,
        }),
      });
      setIsSubmitted(true);
    } catch {
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="sleep-apnea" className="py-20 bg-gray-900 text-white" data-testid="sleep-apnea-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left: Content */}
          <div>
            <span className="inline-block bg-cyan-500/20 text-cyan-400 text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-6">
              Dental Sleep Medicine
            </span>
            <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
              Sleep Apnea & Snoring Treatment
              <span className="block text-cyan-400">Without a CPAP Machine</span>
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed">
              Dr. Maichou Yang is a trained airway dentist specializing in custom oral appliances — 
              a comfortable, silent alternative to CPAP that treats obstructive sleep apnea and eliminates snoring.
            </p>

            {/* Interactive Symptom Checker */}
            <div className="mb-8" data-testid="symptom-checker">
              <h3 className="text-lg font-semibold text-white mb-2">Do you experience any of these?</h3>
              <p className="text-gray-400 text-sm mb-4">Tap to check your symptoms:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {symptoms.map((symptom) => {
                  const isChecked = checkedSymptoms.includes(symptom.id);
                  return (
                    <button
                      key={symptom.id}
                      onClick={() => toggleSymptom(symptom.id)}
                      className={`flex items-center gap-3 rounded-lg px-4 py-3 text-left transition-all duration-300 transform hover:scale-[1.02] ${
                        isChecked 
                          ? 'bg-cyan-500/30 border-2 border-cyan-400 shadow-lg shadow-cyan-500/20' 
                          : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                      }`}
                      data-testid={`symptom-${symptom.id}`}
                    >
                      <div className={`transition-all duration-300 ${isChecked ? 'scale-110' : ''}`}>
                        {isChecked ? (
                          <CheckSquare size={20} className="text-cyan-400 animate-pulse" />
                        ) : (
                          <Square size={20} className="text-gray-500" />
                        )}
                      </div>
                      <symptom.icon size={18} className={`shrink-0 transition-colors duration-300 ${isChecked ? 'text-cyan-300' : 'text-cyan-400'}`} />
                      <span className={`text-sm transition-colors duration-300 ${isChecked ? 'text-white font-medium' : 'text-gray-200'}`}>
                        {symptom.text}
                      </span>
                    </button>
                  );
                })}
              </div>
              
              {/* Dynamic feedback based on checked symptoms */}
              {checkedCount > 0 && (
                <div className={`mt-4 p-4 rounded-lg transition-all duration-500 animate-in fade-in slide-in-from-top-2 ${
                  checkedCount >= 3 ? 'bg-red-500/20 border border-red-400/50' : 'bg-amber-500/20 border border-amber-400/50'
                }`} data-testid="symptom-feedback">
                  <p className={`text-sm font-medium ${checkedCount >= 3 ? 'text-red-300' : 'text-amber-300'}`}>
                    {checkedCount >= 3 ? (
                      <>
                        <span className="font-bold">You checked {checkedCount} symptoms.</span> This suggests a higher risk for sleep apnea. 
                        We recommend scheduling a consultation with Dr. Yang.
                      </>
                    ) : (
                      <>
                        <span className="font-bold">You checked {checkedCount} symptom{checkedCount > 1 ? 's' : ''}.</span> Even one symptom 
                        can indicate sleep-disordered breathing. Take our quiz for a more detailed assessment.
                      </>
                    )}
                  </p>
                </div>
              )}

              {/* Partner Snoring Check */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <button
                  onClick={() => setPartnerSnores(!partnerSnores)}
                  className={`flex items-center gap-3 w-full rounded-lg px-4 py-3 text-left transition-all duration-300 ${
                    partnerSnores 
                      ? 'bg-purple-500/20 border-2 border-purple-400' 
                      : 'bg-white/5 border-2 border-transparent hover:bg-white/10'
                  }`}
                  data-testid="partner-snores-checkbox"
                >
                  {partnerSnores ? (
                    <CheckSquare size={20} className="text-purple-400" />
                  ) : (
                    <Square size={20} className="text-gray-500" />
                  )}
                  <Users size={18} className={partnerSnores ? 'text-purple-300' : 'text-purple-400'} />
                  <span className={`text-sm ${partnerSnores ? 'text-white font-medium' : 'text-gray-300'}`}>
                    My partner also snores or has sleep issues
                  </span>
                </button>
                
                {/* Couples CTA */}
                {partnerSnores && (
                  <div className="mt-3 p-4 bg-purple-500/20 border border-purple-400/50 rounded-lg animate-in fade-in slide-in-from-top-2 duration-300" data-testid="couples-cta">
                    <div className="flex items-start gap-3">
                      <Users size={20} className="text-purple-300 mt-0.5 shrink-0" />
                      <div>
                        <p className="text-purple-200 font-semibold text-sm mb-1">Couples Sleep Consultation Available</p>
                        <p className="text-purple-300/80 text-xs mb-3">
                          When both partners snore, it often indicates a shared airway issue. Dr. Yang offers joint consultations 
                          to evaluate both of you together — often with a discount on dual treatment plans.
                        </p>
                        <a 
                          href="tel:9167731122" 
                          className="inline-flex items-center gap-2 bg-purple-500 hover:bg-purple-600 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors"
                          data-testid="couples-call-button"
                        >
                          <Phone size={14} />
                          Book Couples Consultation
                        </a>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Benefits */}
            <div className="space-y-3 mb-8">
              {[
                'Custom-fitted oral appliances — FDA-cleared & clinically proven',
                'No masks, no noise, no electricity — works anywhere',
                '90%+ nightly compliance vs ~50% for CPAP',
                'Most patients see results from night one',
              ].map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-3">
                  <CheckCircle size={18} className="text-cyan-400 mt-0.5 shrink-0" />
                  <span className="text-gray-300 text-sm">{benefit}</span>
                </div>
              ))}
            </div>

            <button
              onClick={handleTakeQuiz}
              className="inline-flex items-center gap-2 text-cyan-400 hover:text-cyan-300 font-semibold transition-colors"
              data-testid="take-quiz-button"
            >
              Take Our Free Sleep Apnea Risk Quiz
              {checkedCount > 0 && <span className="text-xs bg-cyan-500/30 px-2 py-0.5 rounded-full">Pre-filled</span>}
              <ArrowRight size={18} />
            </button>
          </div>

          {/* Right: Lead Capture Form */}
          <div className="bg-white rounded-2xl p-8 text-gray-900 shadow-2xl" data-testid="sleep-apnea-inline-form">
            {!isSubmitted ? (
              <>
                <h3 className="text-2xl font-bold mb-2">Free Sleep Apnea Consultation</h3>
                <p className="text-gray-500 text-sm mb-6">
                  Fill out the form below and our team will contact you to discuss your symptoms and treatment options.
                </p>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="Your first name"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all"
                      data-testid="sa-section-firstname"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                    <input
                      type="email"
                      required
                      placeholder="you@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all"
                      data-testid="sa-section-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      placeholder="(916) 555-0123"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-cyan-500 outline-none transition-all"
                      data-testid="sa-section-phone"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-cyan-600 hover:bg-cyan-700 disabled:bg-cyan-400 text-white font-bold py-3.5 rounded-lg transition-colors flex items-center justify-center gap-2 text-lg"
                    data-testid="sa-section-submit"
                  >
                    {isSubmitting ? (
                      <><Loader2 size={20} className="animate-spin" /> Sending...</>
                    ) : (
                      'Request Free Consultation'
                    )}
                  </button>
                </form>

                <div className="mt-4 flex items-center justify-center gap-2 text-gray-400 text-xs">
                  <Phone size={12} />
                  <span>Or call directly: <a href="tel:9167731122" className="text-cyan-600 font-semibold hover:underline">(916) 773-1122</a></span>
                </div>

                {/* Social Proof */}
                <div className="mt-6 pt-6 border-t border-gray-100">
                  <p className="text-gray-500 text-xs italic text-center">
                    "I finally sleep through the night. The oral appliance changed my life — and my wife's!" 
                    <span className="block text-gray-400 mt-1 not-italic">— Roseville Patient</span>
                  </p>
                </div>
              </>
            ) : (
              <div className="text-center py-8" data-testid="sa-section-success">
                <CheckCircle size={48} className="mx-auto text-green-500 mb-4" />
                <h3 className="text-xl font-bold mb-2">Thank You!</h3>
                <p className="text-gray-600 mb-4">We'll reach out shortly to discuss your sleep apnea concerns.</p>
                <a href="tel:9167731122" className="inline-flex items-center gap-2 text-cyan-600 font-semibold hover:underline">
                  <Phone size={16} /> Or call now: (916) 773-1122
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Floating Symptom Counter Badge */}
      {showFloatingBadge && checkedCount > 0 && (
        <div 
          className="fixed top-24 left-4 z-40 animate-in slide-in-from-left-4 fade-in duration-300"
          data-testid="floating-symptom-badge"
        >
          <button
            onClick={handleTakeQuiz}
            className="flex items-center gap-3 bg-gray-900 text-white pl-4 pr-5 py-3 rounded-full shadow-xl hover:shadow-2xl transition-all hover:scale-105 border border-cyan-500/30"
          >
            <div className="relative">
              <ClipboardCheck size={20} className="text-cyan-400" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {checkedCount}
              </span>
            </div>
            <div className="text-left">
              <p className="text-xs text-gray-400">Symptoms selected</p>
              <p className="text-sm font-semibold text-cyan-400">Take Quiz →</p>
            </div>
          </button>
        </div>
      )}
    </section>
  );
};

export default SleepApneaSection;
